/*
* === ApplicantDashboardPage.js (With Delete & PDF Export) ===
*/
import React, { useEffect, useState, useCallback } from 'react';
import { 
    Container, Typography, Card, Box, CircularProgress, Alert, Button, 
    Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, 
    List, ListItem, ListItemText, Badge, Stack, CardContent, Grid 
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { isProfileIncomplete } from '../../services/profileGuard';
import { useAuth } from '../../context/AuthContext';
import EditIcon from '@mui/icons-material/Edit';
import MailOutlineIcon from '@mui/icons-material/MailOutline'; 
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CreateIcon from '@mui/icons-material/Create'; 
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import FavoriteIcon from '@mui/icons-material/Favorite';

import WelcomeOnboarding from '../../components/WelcomeOnboarding';
import ContractSigningModal from '../../components/ContractSigningModal';
import ProfileProgressBanner from '../../components/ProfileProgressBanner';
import { tenantConfig } from '../../config/tenantConfig';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// --- Interview Booking Modal ---
const InterviewBookingModal = ({ open, onClose, applicationId, token, onBooked }) => {
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [booking, setBooking] = useState(false);

    useEffect(() => {
        if (open && applicationId) {
            setLoading(true);
            setError('');
            api.get(`/admin/interviews/available/${applicationId}`, { headers: { Authorization: `Bearer ${token}` } })
               .then(res => setSlots(res.data))
               .catch(() => setError('Failed to load slots. Please contact the administrator.'))
               .finally(() => setLoading(false));
        }
    }, [open, applicationId, token]);

    const handleBook = async (slotId) => {
        if(!window.confirm("Confirm this time slot?")) return;
        setBooking(true);
        try {
            await api.put(`/admin/interviews/book/${slotId}`, { applicationId }, { headers: { Authorization: `Bearer ${token}` } });
            alert("Interview Booked! Check your email for details.");
            if (onBooked) onBooked();
            onClose();
        } catch(e) {
            alert(e.response?.data?.message || "Booking failed.");
        } finally {
            setBooking(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Book Your Interview</DialogTitle>
            <DialogContent dividers>
                {loading ? <Box sx={{display:'flex',justifyContent:'center', p: 3}}><CircularProgress/></Box> : 
                 error ? <Alert severity="error">{error}</Alert> :
                 slots.length === 0 ? <Typography align="center" color="text.secondary">No slots available. Please message the admin.</Typography> :
                 <List>
                    {slots.map(slot => (
                        <ListItem key={slot.Id} divider secondaryAction={
                            <Button variant="contained" size="small" onClick={() => handleBook(slot.Id)} disabled={booking}>Book</Button>
                        }>
                            <ListItemText 
                                primary={new Date(slot.StartTime).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                secondary={`${new Date(slot.StartTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${new Date(slot.EndTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`}
                            />
                        </ListItem>
                    ))}
                 </List>
                }
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
    );
};

const ApplicantDashboardPage = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [fullProfile, setFullProfile] = useState(null);
    const [unreadCounts, setUnreadCounts] = useState({});
    const [notifications, setNotifications] = useState([]);
    const [freshName, setFreshName] = useState('');
    
    const { token, user } = useAuth(); 
    const navigate = useNavigate();

    const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedApp, setSelectedApp] = useState(null);
    const [showTour, setShowTour] = useState(false);
    const [signingAppId, setSigningAppId] = useState(null);

    const [careerProfile, setCareerProfile] = useState(null);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [marketPulse, setMarketPulse] = useState({ inDemand: [], future: [], fulfilling: [] });
    const [bookingAppId, setBookingAppId] = useState(null);
    const [pdfLoading, setPdfLoading] = useState({});

    useEffect(() => {
        const hasSeen = localStorage.getItem('hasSeenOnboarding');
        if (!hasSeen) setShowTour(true);
    }, []);

    const fetchData = useCallback(async () => {
        if (!token) { setLoading(false); return; }
        try {
            setLoading(true);
            const [dashboardRes, profileRes] = await Promise.all([
                api.get('/applicant/dashboard', { headers: { Authorization: `Bearer ${token}` } }),
                api.get('/applicant/profile', { headers: { Authorization: `Bearer ${token}` } })
            ]);
            
            const apps = dashboardRes.data;
            setApplications(apps);

            const p = profileRes.data || {};
            setFullProfile(p);
            const nameFromDB = p.FirstNames || p.FirstName || p.FullName || '';
            setFreshName(nameFromDB);

            let counts = {};
            try {
                const inboxRes = await api.get('/messages/inbox', { headers: { Authorization: `Bearer ${token}` } });
                if (inboxRes.data && Array.isArray(inboxRes.data)) {
                    inboxRes.data.forEach(thread => {
                        if (thread.ApplicationId) counts[thread.ApplicationId] = thread.UnreadCount;
                    });
                }
                setUnreadCounts(counts);
            } catch (msgErr) { console.warn("Could not fetch unread messages", msgErr); }

            const newNotifs = [];
            const totalUnread = Object.values(counts).reduce((a, b) => a + b, 0);
            if (totalUnread > 0) {
                newNotifs.push({
                    type: 'info',
                    message: `You have ${totalUnread} new message(s) regarding your applications.`,
                    action: () => navigate('/messages')
                });
            }

            apps.forEach(app => {
                if (app.Status === 'Awarded' || app.Status === 'Approved') {
                    if (app.ContractStatus === 'Signed') {
                        newNotifs.push({
                            type: 'success',
                            message: `All set! Your contract for "${app.FundName || 'your bursary'}" has been successfully signed.`
                        });
                    } else {
                        newNotifs.push({
                            type: 'warning',
                            message: `Action Required: Your application for "${app.FundName || 'your bursary'}" was ${app.Status}. Please sign your contract.`,
                            action: () => setSigningAppId(app.Id)
                        });
                    }
                } else if (app.Status === 'Interview' || app.Status === 'Shortlisted') {
                     newNotifs.push({
                         type: 'warning',
                         message: `Action Required: Please book an interview slot for "${app.FundName || 'your bursary'}".`,
                         action: () => setBookingAppId(app.Id)
                     });
                }
            });
            setNotifications(newNotifs);
        } catch (err) {
            setError(err.response?.data?.message || 'An unexpected error occurred.');
        } finally {
            setLoading(false);
        }
    }, [token, navigate]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        const showGame = tenantConfig.features?.showVibeCheckGame;
        const showPulse = tenantConfig.features?.showMarketPulse;

        if (!showGame && !showPulse) {
            setLoadingProfile(false);
            return;
        }

        const fetchCareerData = async () => {
            setLoadingProfile(true);
            const headers = { Authorization: `Bearer ${token}` };

            const [profileResult, pulseResult] = await Promise.allSettled([
                showGame ? api.get('/aptitude/profile', { headers }) : Promise.resolve({ data: null }),
                showPulse ? api.get('/careers/weekly-pulse', { headers }) : Promise.resolve({ data: null })
            ]);

            if (profileResult.status === 'fulfilled' && profileResult.value?.data?.hasProfile) {
                setCareerProfile(profileResult.value.data);
            }

            if (pulseResult.status === 'fulfilled' && pulseResult.value?.data) {
                setMarketPulse({
                    inDemand: pulseResult.value.data.inDemand || [],
                    future: pulseResult.value.data.future || [],
                    fulfilling: pulseResult.value.data.fulfilling || []
                });
            }

            setLoadingProfile(false);
        };
        
        if (token) fetchCareerData();
    }, [token]);
    
    const getStatusChipColor = (status) => {
        switch (status) {
            case 'Approved': case 'Awarded': case 'Funded': return 'success';
            case 'Rejected': return 'error';
            case 'Under Review': case 'Interview': case 'Shortlisted': return 'warning';
            case 'Submitted': return 'info';
            case 'Withdrawn': return 'default';
            default: return 'default';
        }
    };

    const handleOpenConversation = (app) => {
        navigate(`/messages?open_thread=${app.Id}`);
    };

    const handleOpenWithdrawModal = (app) => { setSelectedApp(app); setIsWithdrawModalOpen(true); };
    const handleCloseWithdrawModal = () => { setSelectedApp(null); setIsWithdrawModalOpen(false); };
    const handleConfirmWithdraw = async () => {
        if(!selectedApp) return;
        try { await api.put(`/applicant/application/${selectedApp.Id}/withdraw`, {}, { headers: { Authorization: `Bearer ${token}` } }); fetchData(); handleCloseWithdrawModal(); }
        catch(e) { setError('Failed to withdraw.'); handleCloseWithdrawModal(); }
    };

    const handleOpenDeleteModal = (app) => { setSelectedApp(app); setIsDeleteModalOpen(true); };
    const handleCloseDeleteModal = () => { setSelectedApp(null); setIsDeleteModalOpen(false); };
    const handleConfirmDelete = async () => {
        if (!selectedApp) return;
        try {
            await api.delete(`/applicant/application/${selectedApp.Id}`, { headers: { Authorization: `Bearer ${token}` } });
            fetchData();
            handleCloseDeleteModal();
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to delete application.');
            handleCloseDeleteModal();
        }
    };

    const handleDownloadPdf = async (appId) => {
        setPdfLoading(prev => ({ ...prev, [appId]: true }));
        try {
            const res = await api.get(`/applicant/application/${appId}/export-pdf`, { 
                headers: { Authorization: `Bearer ${token}` } 
            });
            const data = res.data;
            const doc = new jsPDF();
    
            const primaryColor = [44, 54, 94]; 
            const secondaryColor = [40, 40, 40];
    
            doc.setFontSize(18);
            doc.setTextColor(...primaryColor);
            doc.text(`${data.companyName}`, 14, 18);
    
            doc.setFontSize(14);
            doc.setTextColor(...secondaryColor);
            doc.text(`Official Application Summary — ${data.fundName}`, 14, 25);
    
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            doc.text(`Reference No: ${data.applicationRef}   |   Status: ${data.status}`, 14, 31);
            doc.text(`Submitted On: ${data.submittedAt ? new Date(data.submittedAt).toLocaleString() : 'N/A'}`, 14, 36);
    
            doc.setLineWidth(0.5);
            doc.setDrawColor(200, 200, 200);
            doc.line(14, 39, 196, 39);
    
            let currentY = 44;
    
            const p = data.personal || {};
            const addr = p.address || {};
            
            const formattedAddress = [addr.street, addr.suburb, addr.city, addr.province, addr.postalCode]
                .filter(Boolean)
                .join(', ') || 'N/A';
    
            const personalRows = [
                ['Full Names', `${p.firstNames} ${p.surname}`.trim() || 'N/A'],
                ['ID / Passport Number', p.idNumber || 'N/A'],
                ['Gender & Race', `${p.gender || 'N/A'} / ${p.race || 'N/A'}`],
                ['Citizenship', p.citizenship || 'South Africa'],
                ['Contact Number', p.cellNumber || 'N/A'],
                ['Email Address', p.email || user?.email || 'N/A'],
                ['Address', formattedAddress]
            ];
    
            autoTable(doc, {
                startY: currentY,
                head: [['1. Personal & Contact Details', 'Information']],
                body: personalRows,
                theme: 'striped',
                headStyles: { fillColor: primaryColor }
            });
    
            currentY = doc.lastAutoTable.finalY + 8;
    
            const fin = data.financial || {};
            const g = fin.guardian || {};
            const fInfo = fin.financial || {};
    
            const financialRows = [
                ['Guardian / Next of Kin Name', `${g.firstName || g.name || ''} (${g.relation || 'Guardian'})`],
                ['Guardian Contact & Email', `${g.cellNumber || g.phone || 'N/A'} | ${g.email || 'N/A'}`],
                ['Employer / Occupation', g.employer || 'N/A'],
                ['Household Income Category', fInfo.householdIncome || fInfo.income || 'N/A'],
                ['Number of Dependants', String(fInfo.dependants || 'N/A')]
            ];
    
            autoTable(doc, {
                startY: currentY,
                head: [['2. Guardian & Financial Details', 'Information']],
                body: financialRows,
                theme: 'striped',
                headStyles: { fillColor: primaryColor }
            });
    
            currentY = doc.lastAutoTable.finalY + 8;
    
            const ac = data.academic || {};
            const academicList = Array.isArray(ac) ? ac : (ac.results || []);
            
            if (academicList.length > 0) {
                const academicRows = academicList.map(item => [
                    item.Level || 'Matric',
                    item.Subject || item.moduleName || 'Subject',
                    `${item.Grade || item.mark || 0}%`
                ]);
    
                autoTable(doc, {
                    startY: currentY,
                    head: [['3. Academic Level', 'Subject / Module', 'Score / Grade']],
                    body: academicRows,
                    theme: 'striped',
                    headStyles: { fillColor: primaryColor }
                });
    
                currentY = doc.lastAutoTable.finalY + 8;
            }
    
            const jbm = data.jbmarks || {};
            if (Object.keys(jbm).length > 0) {
                const jbmRows = Object.entries(jbm).map(([key, val]) => [
                    key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
                    typeof val === 'object' ? JSON.stringify(val) : String(val)
                ]);
    
                autoTable(doc, {
                    startY: currentY,
                    head: [['4. Custom Program Application Fields', 'Response']],
                    body: jbmRows,
                    theme: 'striped',
                    headStyles: { fillColor: primaryColor }
                });
    
                currentY = doc.lastAutoTable.finalY + 8;
            }
    
            if (data.motivation) {
                if (currentY > 230) { doc.addPage(); currentY = 20; }
    
                doc.setFontSize(11);
                doc.setTextColor(...primaryColor);
                doc.text("5. Personal Motivation / Essay Statement", 14, currentY);
    
                doc.setFontSize(9);
                doc.setTextColor(60, 60, 60);
                const splitText = doc.splitTextToSize(data.motivation, 180);
                doc.text(splitText, 14, currentY + 6);
    
                currentY = currentY + 10 + (splitText.length * 4);
            }
    
            if (currentY > 220) { doc.addPage(); currentY = 20; }
    
            const docList = data.documents || [];
            const docRows = docList.length > 0 
                ? docList.map(d => [
                    d.FileType || 'Document', 
                    d.FileName || 'Attached File', 
                    d.IsVerified ? 'Verified ✓' : 'Submitted (Pending Review)'
                  ])
                : [['N/A', 'No document files linked', 'N/A']];
    
            autoTable(doc, {
                startY: currentY,
                head: [['6. Submitted Document Inventory', 'Filename', 'Verification Status']],
                body: docRows,
                theme: 'striped',
                headStyles: { fillColor: primaryColor }
            });
    
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(150, 150, 150);
                doc.text(`Page ${i} of ${pageCount}`, 14, 287);
                doc.text(`Powered by BrightSparc Infrastructure (https://brightsparc.co.za)`, 105, 287);
            }
    
            doc.save(`${data.applicationRef || 'Application'}.pdf`);
    
        } catch (e) {
            console.error("PDF Export error:", e);
            alert("Failed to export complete application PDF.");
        } finally {
            setPdfLoading(prev => ({ ...prev, [appId]: false }));
        }
    };

    const handleLaunchGame = () => { 
        navigate('/aptitude-game'); 
    };

    // 🚀 DYNAMIC MULTI-LINE FEEDBACK RENDERER
    // === src/client/pages/ApplicantDashboardPage.js ===

const columns = [
    { 
        field: 'FundName', 
        headerName: 'Bursary Program', 
        flex: 2.5, 
        minWidth: 320,
        renderCell: (params) => {
            const feedbackText = params.row.Feedback || params.row.feedback || params.row.StatusReason;
            const requestedDocs = params.row.RequestedDocuments;

            return (
                <Box sx={{ py: 1, width: '100%', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    <Typography 
                        variant="body2" 
                        fontWeight="bold" 
                        sx={{ color: '#1e293b', mb: (feedbackText || requestedDocs) ? 1 : 0 }}
                    >
                        {params.row.FundName || 'Unnamed Bursary Program'}
                    </Typography>
                    
                    {/* 🚀 1. ACTION REQUIRED: REQUESTED DOCUMENTS CALLOUT */}
                    {requestedDocs && (
                        <Box 
                            sx={{ 
                                p: 1.25, 
                                mb: feedbackText ? 1 : 0,
                                bgcolor: '#fffbeb', 
                                borderLeft: '4px solid #f59e0b', 
                                borderRadius: '6px',
                                width: '100%'
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 'bold' }}>
                                    Action Required — Missing / Updated Docs:
                                </Typography>
                                <Button 
                                    size="small" 
                                    variant="contained" 
                                    color="warning"
                                    onClick={() => navigate('/applicant/documents')}
                                    sx={{ fontSize: '0.7rem', py: 0.25, px: 1, textTransform: 'none' }}
                                >
                                    Upload to Vault
                                </Button>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#78350f', display: 'block', fontWeight: 'medium' }}>
                                {(() => {
                                    try {
                                        return JSON.parse(requestedDocs).join(', ');
                                    } catch (e) {
                                        return requestedDocs;
                                    }
                                })()}
                            </Typography>
                        </Box>
                    )}

                    {/* 🚀 2. CLEAN MULTI-LINE FEEDBACK CARD */}
                    {feedbackText && (
                        <Box 
                            sx={{ 
                                p: 1.25, 
                                bgcolor: params.row.Status === 'Rejected' ? '#fef2f2' : '#f0f9ff', 
                                borderLeft: `4px solid ${params.row.Status === 'Rejected' ? '#ef4444' : '#0284c7'}`, 
                                borderRadius: '6px',
                                width: '100%'
                            }}
                        >
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: params.row.Status === 'Rejected' ? '#991b1b' : '#0369a1', 
                                    fontWeight: 'bold', 
                                    display: 'block', 
                                    mb: 0.25 
                                }}
                            >
                                Funder Feedback / Action Required:
                            </Typography>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: params.row.Status === 'Rejected' ? '#7f1d1d' : '#334155', 
                                    lineHeight: 1.4, 
                                    display: 'block' 
                                }}
                            >
                                {feedbackText}
                            </Typography>
                        </Box>
                    )}
                </Box>
            );
        }
    },
        { 
            field: 'SubmittedAt', 
            headerName: 'Submitted', 
            width: 120, 
            valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString() : 'Draft' 
        },
        { 
            field: 'Status', 
            headerName: 'Status', 
            width: 120, 
            renderCell: (p) => <Chip label={p.value} color={getStatusChipColor(p.value)} size="small" /> 
        },
        {
            field: 'actions', 
            headerName: 'Actions', 
            width: 280, 
            sortable: false,
            renderCell: (params) => {
                const status = params.row.Status;
                const fundId = params.row.FundId;
                const appId = params.row.Id;
                
                const closingDate = params.row.ApplicationEndDate 
                    ? new Date(params.row.ApplicationEndDate) 
                    : new Date('2026-07-31T23:59:59');
                const now = new Date();
                const isClosed = now > closingDate;

                const canEdit = (status === 'Draft' || status === 'Submitted') && !isClosed;
                const unread = unreadCounts[appId] || 0;

                return (
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', pt: 0.5 }}>
                        {status === 'Draft' && isClosed ? (
                            <Button variant="outlined" size="small" color="error" disabled>
                                Closed
                            </Button>
                        ) : canEdit && (
                            <Button 
                                variant="contained" 
                                size="small" 
                                startIcon={<EditIcon/>}
                                onClick={async () => {
                                    try {
                                        const res = await api.get('/applicant/profile', { headers: { Authorization: `Bearer ${token}` } });
                                        if (!res.data || isProfileIncomplete(res.data)) { 
                                            navigate('/profile?reason=incomplete_profile');
                                        } else {
                                            navigate(`/applicant/form?fundId=${fundId}`);
                                        }
                                    } catch (err) {
                                        navigate('/profile?reason=incomplete_profile');
                                    }
                                }}
                            >
                                {status === 'Draft' ? 'Continue' : 'Edit'}
                            </Button>
                        )}

                        {['Submitted', 'Under Review', 'Interview', 'Shortlisted', 'Approved', 'Awarded', 'Funded'].includes(status) && (
                            <IconButton 
                                onClick={() => handleDownloadPdf(appId)} 
                                color="primary" 
                                size="small"
                                title="Download Application PDF"
                                disabled={!!pdfLoading[appId]}
                            >
                                {pdfLoading[appId] ? <CircularProgress size={18} /> : <PictureAsPdfIcon />}
                            </IconButton>
                        )}

                        {['Awarded','Approved'].includes(status) && (
                            params.row.ContractStatus === 'Signed' ? (
                                <Button variant="contained" size="small" color="success" disabled>Signed</Button>
                            ) : (
                                <Button variant="outlined" size="small" color="success" startIcon={<CreateIcon/>} onClick={()=>setSigningAppId(appId)}>Sign</Button>
                            )
                        )}

                        {['Shortlisted', 'Interview'].includes(status) && (
                            <Button variant="contained" size="small" color="warning" startIcon={<EventAvailableIcon />} onClick={() => setBookingAppId(appId)}>Book</Button>
                        )}

                        <IconButton onClick={() => handleOpenConversation(params.row)} size="small">
                            <Badge badgeContent={unread} color="error"><MailOutlineIcon/></Badge>
                        </IconButton>

                        {['Submitted', 'Under Review'].includes(status) && (
                            <IconButton onClick={() => handleOpenWithdrawModal(params.row)} color="warning" size="small" title="Withdraw Application">
                                <CancelIcon />
                            </IconButton>
                        )}

                        {['Draft', 'Withdrawn'].includes(status) && (
                            <IconButton onClick={() => handleOpenDeleteModal(params.row)} color="error" size="small" title="Delete Permanently">
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </Box>
                );
            }
        }
    ];

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    
    const displayName = freshName || user?.firstNames || user?.firstName || user?.fullName || (user?.email ? user.email.split('@')[0] : 'Applicant');

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                    Welcome, {displayName}!
                </Typography>
                <Box>
                    <Button startIcon={<HelpOutlineIcon />} onClick={() => setShowTour(true)} sx={{ mr: 2 }}>
                        Guide Me
                    </Button>
                    <Button component={Link} to="/" variant="outlined">Find More Bursaries</Button>
                </Box>
            </Box>

            <ProfileProgressBanner userProfile={fullProfile} />

            {notifications.length > 0 && (
                <Stack spacing={2} sx={{ mb: 3 }}>
                    {notifications.map((note, index) => (
                        <Alert 
                            key={index} 
                            severity={note.type} 
                            icon={note.type === 'info' ? <NotificationsActiveIcon /> : undefined}
                            action={ note.action ? <Button color="inherit" size="small" onClick={note.action}>View</Button> : null }
                        >
                            {note.message}
                        </Alert>
                    ))}
                </Stack>
            )}

            <Typography variant="h5" sx={{ mb: 2 }}>My Applications</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* 🚀 AUTO-EXPANDING DATAGRID FOR MULTI-LINE FEEDBACK */}
            <Card>
                <Box sx={{ width: '100%' }}>
                    <DataGrid 
                        rows={applications} 
                        columns={columns} 
                        getRowId={(row) => row.Id} 
                        loading={loading} 
                        autoHeight
                        getRowHeight={() => 'auto'}
                        sx={{
                            '& .MuiDataGrid-cell': {
                                py: 1.5,
                                alignItems: 'flex-start'
                            }
                        }}
                    />
                </Box>

            </Card>

            {/* ========================================================================= */}
            {/* 1. AI CAREER VIBE CHECK GAME SECTION */}
            {/* ========================================================================= */}
            {tenantConfig.features?.showVibeCheckGame && (
                <>
                    {loadingProfile ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                            <CircularProgress color="secondary" />
                        </Box>
                    ) : careerProfile ? (
                        <Card 
                            elevation={2} 
                            sx={{ 
                                mt: 4, mb: 4, borderRadius: 4, 
                                background: 'linear-gradient(135deg, #4a148c 0%, #7b1fa2 100%)', 
                                color: 'white'
                            }}
                        >
                            <CardContent sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', p: 4, '&:last-child': { pb: 4 } }}>
                                <Box sx={{ flex: 1, pr: { md: 4 }, textAlign: { xs: 'center', md: 'left' } }}>
                                    <Typography variant="overline" sx={{ letterSpacing: 2, color: '#e1bee7', fontWeight: 'bold' }}>
                                        Your Career Intelligence Profile
                                    </Typography>
                                    <Typography variant="h4" fontWeight="900" gutterBottom sx={{ mt: 1 }}>
                                        {careerProfile.archetype}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#f3e5f5', fontSize: '1.05rem', lineHeight: 1.6, mb: 3 }}>
                                        {careerProfile.message}
                                    </Typography>
            
                                    {careerProfile.careers && careerProfile.careers.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2" sx={{ color: '#e1bee7', mb: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
                                                Suggested Career Paths:
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                                                {careerProfile.careers.map((career, idx) => (
                                                    <Chip 
                                                        key={idx} 
                                                        label={career} 
                                                        sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 'bold', backdropFilter: 'blur(4px)' }} 
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                                
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: { xs: 3, md: 0 }, minWidth: 220 }}>
                                    <AutoAwesomeIcon sx={{ fontSize: 60, color: '#ffeb3b', mb: 1 }} />
                                    <Chip label="Verified by Gemini AI" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold', mb: 3 }} />
                                    
                                    <Button 
                                        variant="contained" 
                                        component={Link} 
                                        to={`/?field=${encodeURIComponent(careerProfile.recommended_field || '')}`} 
                                        sx={{ 
                                            bgcolor: 'white', 
                                            color: '#4a148c', 
                                            fontWeight: 'bold', 
                                            '&:hover': { bgcolor: '#f3e5f5' },
                                            width: '100%'
                                        }}
                                    >
                                        Find Matching Bursaries
                                    </Button>
                                </Box>
                            </CardContent>    
                        </Card>
                    ) : (
                        <Card elevation={0} sx={{ mt: 4, mb: 4, borderRadius: 4, background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)', border: '1px solid #ce93d8' }}>
                            <CardContent sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'space-between', p: { xs: 3, md: 4 }, '&:last-child': { pb: { xs: 3, md: 4 } } }}>
                                <Box sx={{ flex: 1, pr: { md: 4 }, textAlign: { xs: 'center', md: 'left' }, mb: { xs: 3, md: 0 } }}>
                                    <Typography variant="h5" fontWeight="800" sx={{ color: '#4a148c' }} gutterBottom>
                                        Unsure about your future?
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontSize: '1.1rem', color: '#6a1b9a' }}>
                                        Play our 60-second Vibe Check game and let the <strong>Career Intelligence AI</strong> map your personality and problem-solving skills to the highest-demand jobs in South Africa.
                                    </Typography>
                                </Box>
                                <Button 
                                    variant="contained" 
                                    color="secondary" 
                                    size="large" 
                                    startIcon={<AutoAwesomeIcon />} 
                                    onClick={handleLaunchGame} 
                                    sx={{ borderRadius: 8, px: 4, py: 1.5, whiteSpace: 'nowrap', fontWeight: 'bold', textTransform: 'none', fontSize: '1.05rem', boxShadow: 4 }}
                                >
                                    Launch AI Matchmaker
                                </Button>
                            </CardContent>
                        </Card>                
                    )}
                </>
            )}
            
            {/* ========================================================================= */}
            {/* 2. WEEKLY MARKET PULSE SECTION */}
            {/* ========================================================================= */}
            {tenantConfig.features?.showMarketPulse && (
                <Box sx={{ mt: 4, mb: 4 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUpIcon color="primary" /> Weekly Market Pulse
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Live updates on South Africa's labor market to help guide your studies.
                    </Typography>
            
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Card elevation={2} sx={{ height: '100%', borderTop: '4px solid #f44336' }}>
                                <CardContent>
                                    <Typography variant="subtitle1" fontWeight="bold" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <TrendingUpIcon /> High Demand
                                    </Typography>
                                    <List disablePadding>
                                        {marketPulse.inDemand.map((job, idx) => (
                                            <ListItem key={idx} disableGutters divider={idx !== 2}>
                                                <ListItemText primary={job.job_title} secondary={`${job.sector} • ${job.demand_level}`} primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.9rem' }} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
            
                        <Grid item xs={12} md={4}>
                            <Card elevation={2} sx={{ height: '100%', borderTop: '4px solid #2196f3' }}>
                                <CardContent>
                                    <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <RocketLaunchIcon /> Emerging Jobs
                                    </Typography>
                                    <List disablePadding>
                                        {marketPulse.future.map((job, idx) => (
                                            <ListItem key={idx} disableGutters divider={idx !== 2}>
                                                <ListItemText primary={job.sa_equivalent_title || job.global_job_title} secondary={job.sa_sector} primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.9rem' }} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
            
                        <Grid item xs={12} md={4}>
                            <Card elevation={2} sx={{ height: '100%', borderTop: '4px solid #4caf50' }}>
                                <CardContent>
                                    <Typography variant="subtitle1" fontWeight="bold" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <FavoriteIcon /> High Impact
                                    </Typography>
                                    <List disablePadding>
                                        {marketPulse.fulfilling.map((job, idx) => (
                                            <ListItem key={idx} disableGutters divider={idx !== 2}>
                                                <ListItemText primary={job.job_title} secondary={`Score: ${job.fulfillment_score}/100 • ${job.sector}`} primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.9rem' }} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Withdraw Modal */}
            <Dialog open={isWithdrawModalOpen} onClose={handleCloseWithdrawModal}>
                <DialogTitle>Confirm Withdrawal</DialogTitle>
                <DialogContent>
                    <Typography>Withdraw application for <strong>{selectedApp?.FundName || 'this bursary'}</strong>?</Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>This action cannot be undone.</Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseWithdrawModal}>Cancel</Button>
                    <Button onClick={handleConfirmWithdraw} color="error" variant="contained">Yes, Withdraw</Button>
                </DialogActions>
            </Dialog>

            {/* DELETE CONFIRMATION MODAL */}
            <Dialog open={isDeleteModalOpen} onClose={handleCloseDeleteModal}>
                <DialogTitle sx={{ color: 'error.main' }}>Permanently Delete Application?</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete your application for <strong>{selectedApp?.FundName || 'this bursary'}</strong>?</Typography>
                    <Alert severity="error" sx={{ mt: 2 }}>
                        This will permanently remove your draft, attached documents, and progress. You will need to start fresh if you wish to apply again.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteModal}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained" startIcon={<DeleteIcon />}>
                        Delete Permanently
                    </Button>
                </DialogActions>
            </Dialog>

            <WelcomeOnboarding open={showTour} onClose={() => setShowTour(false)} />
            <ContractSigningModal open={!!signingAppId} onClose={() => setSigningAppId(null)} applicationId={signingAppId} onSigned={fetchData} />
            <InterviewBookingModal open={!!bookingAppId} onClose={() => setBookingAppId(null)} applicationId={bookingAppId} token={token} onBooked={fetchData} />
        </Container>
    );
};

export default ApplicantDashboardPage;
